// עריכה בשפה חופשית: הוראה → job → תקציר + תצוגה מקדימה מול הנוכחי → החלה (גרסה חדשה) או ביטול.
// ההצעה לא נשמרת עד apply. הצעה שהתיישנה נדחית ומוצעת הרצה מחדש.
import { useEffect, useState } from 'react';
import { describeOps, type BannerDoc, type EditOp } from '@bb/schema';
import { api, ApiError, waitForJob, type Job } from '../../api';
import { ErrorBox, JobBadge } from '../ui';
import BannerView, { fitScale } from './BannerView';
import type { AssetMap } from './types';

interface Proposal { proposal: { baseRevision: number; summary: string; ops: EditOp[] }; preview: BannerDoc; lines?: string[] }

interface Props {
  variantId: string;
  revision: number;
  doc: BannerDoc;
  assets: AssetMap;
  flush: () => Promise<number | null>;
  onApplied: (revision: number, doc: BannerDoc) => void;
}

export function useAiStatus() {
  const [s, setS] = useState<{ provider: string; model?: string; mock: boolean; keyConfigured?: boolean; verified?: boolean; lastError?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.get('/api/ai/status').then(setS).catch((e) => setErr(e.message)); }, []);
  return { status: s, error: err };
}

export function AiStatusNote({ status, error }: ReturnType<typeof useAiStatus>) {
  if (error) return <div className="small muted">מצב ספק AI לא זמין ({error})</div>;
  if (!status) return null;
  if (status.mock) return <div className="mock-banner small" role="note">ספק AI במצב mock — התוצאה אינה תוצר של מודל אמיתי.</div>;
  return <div className="small muted">ספק: <span className="mono">{status.provider}</span>{status.model ? <> · <span className="mono">{status.model}</span></> : null}{status.verified === false ? ' · החיבור טרם אומת' : ''}</div>;
}

export default function AiPanel({ variantId, revision, doc, assets, flush, onApplied }: Props) {
  const ai = useAiStatus();
  const [instruction, setInstruction] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Proposal | null>(null);
  const [applied, setApplied] = useState<{ base: number; revision: number } | null>(null);
  const [replay, setReplay] = useState(0);
  const [stale, setStale] = useState(false);

  const run = async () => {
    setError(null); setResult(null); setStale(false); setApplied(null);
    if (!instruction.trim()) { setError('יש לכתוב הוראה'); return; }
    setBusy(true);
    try {
      const base = await flush();
      if (base === null) { setError('יש שינויים שלא נשמרו או התנגשות. שמרו קודם ואז הריצו שוב.'); return; }
      const { jobId } = await api.post<{ jobId: string }>(`/api/variants/${variantId}/ai-edit`, { instruction, baseRevision: base });
      const j = await waitForJob(jobId, setJob);
      if (j.status !== 'succeeded') { setError(`עריכת AI נכשלה: ${j.error ?? j.status}. הטיוטה שלכם לא השתנתה.`); return; }
      setResult(j.result as Proposal);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!result) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ revision: number }>(`/api/variants/${variantId}/ai-edit/apply`, { baseRevision: result.proposal.baseRevision, ops: result.proposal.ops, summary: result.proposal.summary });
      const rev = await api.get<{ revision: number; doc: BannerDoc }>(`/api/variants/${variantId}/revisions/${r.revision}`);
      onApplied(rev.revision, rev.doc);
      setApplied({ base: result.proposal.baseRevision, revision: r.revision });
      setResult(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setStale(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const undoApplied = async () => {
    if (!applied) return;
    setBusy(true); setError(null);
    try {
      const cur = await flush();
      if (cur === null) { setError('יש שינויים שלא נשמרו או התנגשות.'); return; }
      const r = await api.post<{ revision: number; doc: BannerDoc }>(`/api/variants/${variantId}/revert`, { toRevision: applied.base, expectedRevision: cur });
      onApplied(r.revision, r.doc);
      setApplied(null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  const isStale = stale || (!!result && result.proposal.baseRevision !== revision);
  const sc = fitScale(doc.width, doc.height, 330, 300);
  const lines = result ? (result.lines?.length ? result.lines : describeOps(result.proposal.ops)) : [];

  return (
    <section className="ed-panel" aria-labelledby="ai-h">
      <h3 id="ai-h" className="ed-h">עריכה בשפה חופשית (AI)</h3>
      <AiStatusNote {...ai} />
      <label className="pf"><span>הוראה</span>
        <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3} maxLength={2000} placeholder="לדוגמה: הגדל כותרת ב-15 אחוז והשהה CTA עד שנייה 4" />
      </label>
      <div className="row">
        <button className="btn primary" onClick={run} disabled={busy}>{busy && !result ? 'מריץ…' : 'הצע שינוי'}</button>
        {job && <JobBadge status={job.status} />}
        <span className="small muted">בסיס: גרסה <span className="mono">{revision}</span>. טקסט נעול לא ייערך.</span>
      </div>
      <ErrorBox error={error} />
      {result && (
        <div className="ai-result" aria-live="polite">
          <h4 className="ed-h">הצעה {result.proposal.summary && <span className="muted small">— {result.proposal.summary}</span>}</h4>
          <ul className="small">{lines.map((x, i) => <li key={i}>{x}</li>)}</ul>
          <div className="ai-compare">
            <figure><figcaption className="small muted">נוכחי (גרסה {revision})</figcaption><BannerView doc={doc} assets={assets} autoplay scale={sc} replayKey={replay} label="הבאנר הנוכחי" /></figure>
            <figure><figcaption className="small muted">אחרי ההצעה (לא נשמר)</figcaption><BannerView doc={result.preview} assets={assets} autoplay scale={sc} replayKey={replay} label="תצוגת ההצעה" /></figure>
          </div>
          {isStale && <div className="alert warn small">הבאנר השתנה מאז שההצעה חושבה (בסיס {result.proposal.baseRevision}, כעת {revision}). כדי לא לדרוס עריכה ידנית, ההצעה לא תוחל. אפשר להריץ שוב על הגרסה הנוכחית.</div>}
          <div className="row">
            <button className="btn" onClick={() => setReplay((x) => x + 1)}>↻ ניגון חוזר</button>
            <button className="btn primary" onClick={apply} disabled={busy || isStale}>החל (גרסה חדשה)</button>
            <button className="btn" onClick={() => { setResult(null); setStale(false); }} disabled={busy}>בטל הצעה</button>
            {isStale && <button className="btn" onClick={run} disabled={busy}>הרץ שוב</button>}
          </div>
        </div>
      )}
      {applied && (
        <div className="alert ok small" role="status">
          העריכה הוחלה כגרסה <span className="mono">{applied.revision}</span>. אפשר לבטל ב-Ctrl+Z (נשמר כגרסה חדשה) או
          {' '}<button className="linkish" onClick={undoApplied} disabled={busy}>לחזור לגרסה {applied.base}</button>.
        </div>
      )}
    </section>
  );
}
